// Static sample experiment used by:
//   - /start    → "What a report experiment looks like" preview
//   - landing   → mirrored in page.js with equivalent styling
//
// Matches the JSON shape produced by the paid-report LLM pipeline so the same
// <ExperimentCard /> component can render it unchanged. Update in one place.
const sampleExperiment = {
  id: "ctr-fix-free-ai-email-generator",
  track: "ctr-fix",
  priority: "high",
  score: 93,
  title: "Free AI Email Generator — CTR rewrite",
  url: "/tools/free-ai-email-generator",
  action: "Update title + meta to capture 'no login' and 'in seconds' intent",
  impact: "~1,400–2,100 extra clicks/month",
  gsc_signal: {
    impressions: 217597,
    clicks: 2422,
    ctr: 0.0111,
    position: 10.2,
    period_days: 90,
    monthly_impressions: 72500,
  },
  scoring: { demand: 35, gap: 28, effort: 20, fit: 10, total: 93 },
  target_queries: [
    { query: "ai email generator", impressions: 32855, current_ctr: 0.0075, target_ctr_min: 0.03, target_ctr_max: 0.05 },
    { query: "ai email writer", impressions: 13277, current_ctr: 0.0040, target_ctr_min: 0.02, target_ctr_max: 0.03 },
    { query: "email generator", impressions: 12604, current_ctr: 0.0146, target_ctr_min: 0.04, target_ctr_max: 0.06 },
    { query: "ai email generator free", impressions: 10381, current_ctr: 0.0119, target_ctr_min: 0.05, target_ctr_max: 0.08 },
    { query: "professional email generator", impressions: 1546, current_ctr: 0.0052, target_ctr_min: 0.03, target_ctr_max: 0.05 },
  ],
  current_state: {
    title: "Free AI Email Generator",
    meta: "Generate emails with AI",
  },
  proposed_changes: {
    title: "Free AI Email Generator — Write Professional Emails in Seconds, No Login",
    meta: "Generate polished, professional emails instantly. Enter your topic, tone, and recipient — our AI writes the perfect email in seconds. 100% free, no signup, unlimited use.",
    h1: null,
    content_additions: [],
    faq_additions: [
      { q: "Is this AI email generator free?", a: "Yes — completely free, no signup required, unlimited uses." },
      { q: "Can I generate professional business emails?", a: "Yes. Pick the Professional tone and fill in the context; the AI produces a polished business-ready email in seconds." },
      { q: "Does this work for meeting and appointment emails?", a: "Yes — confirmation, reminder, rescheduling, and cancellation templates are all supported." },
    ],
    schema_additions: ["FAQPage"],
  },
  todo: [
    "Change title to: 'Free AI Email Generator — Write Professional Emails in Seconds, No Login'",
    "Change meta description to the proposed copy above",
    "Add FAQPage JSON-LD with the 3 Q&A items",
    "Deploy and open GSC Performance → URL filter to monitor CTR",
    "Check CTR after 14 days — target 3–4% on this URL",
  ],
  reasoning: [
    "Competitors ranking 1–3 all use 'Free', 'No Login', 'in Seconds' in their titles — current title lacks these trust + urgency signals.",
    "'Professional' matches a high-intent query (1,546 imp, 0.52% CTR) that's barely converting now.",
    "'No Login' directly targets 'ai email generator free' variants (10,381 imp).",
    "FAQ schema can trigger rich snippets — stacked wins from position 10 + better snippet.",
  ],
  competitor_signals: [
    { competitor: "QuillBot", title: "Free AI Email Writer: Write Professional Emails in Seconds" },
    { competitor: "Toolbaz",  title: "[FREE] AI Email Writer - (No Login & Unlimited)" },
    { competitor: "Writecream", title: "AI Email Writer [100% Free, No Login]" },
  ],
  expected_impact: {
    ctr_from: 0.0111,
    ctr_to_min: 0.03,
    ctr_to_max: 0.04,
    monthly_clicks_from: 800,
    monthly_clicks_to_min: 2200,
    monthly_clicks_to_max: 2900,
    additional_clicks_monthly: 1750,
  },
  claude_prompt: `You are helping implement an SEO CTR fix on /tools/free-ai-email-generator.

Find the page component (likely in app/javascript/ or src/pages/tools/).

1. Update the <title> to:
   Free AI Email Generator — Write Professional Emails in Seconds, No Login

2. Update the <meta name="description"> to:
   Generate polished, professional emails instantly. Enter your topic, tone, and recipient — our AI writes the perfect email in seconds. 100% free, no signup, unlimited use.

3. Add FAQPage JSON-LD schema with these Q&A pairs:
   - Is this AI email generator free?
   - Can I generate professional business emails?
   - Does this work for meeting and appointment emails?

Target queries (ranked by volume): ai email generator (32.8k/90d), ai email writer (13.3k), email generator (12.6k).
Current CTR 1.11% at position 10.2 — target 3–4% after 14 days.`,
};

export default sampleExperiment;
